/**
 * Prepare block data for sending to backend
 * This ensures all necessary fields are included based on block type
 * and excludes fields that cause validation errors
 */
export const prepareBlockDataForBackend = (block) => {
  // Only include fields that the backend expects and validates
  const baseData = {
    type: block.type,
    display_order:
      typeof block.display_order === "number" ? block.display_order : 0,
  };

  // Add optional fields only if they have values
  if (block.column) {
    baseData.column = block.column;
  }

  if (block.parent_block_id) {
    baseData.parent_block_id = block.parent_block_id;
  }

  // Handle layout-specific data
  if (block.layout === "two-column") {
    return {
      ...baseData,
      type: "two-column", // Ensure type is set correctly
      layout: "two-column",
      leftBlock: block.leftBlock
        ? prepareBlockDataForBackend(block.leftBlock)
        : null,
      rightBlock: block.rightBlock
        ? prepareBlockDataForBackend(block.rightBlock)
        : null,
    };
  }

  // Add type-specific data based on what the backend expects
  switch (block.type) {
    case "image":
      return {
        ...baseData,
        url: block.url || "",
        alt: block.alt || "",
        caption: block.caption || "",
        style: block.style || {},
      };

    case "video":
      return {
        ...baseData,
        url: block.url || "",
        title: block.title || "",
        thumbnail: block.thumbnail || "",
        style: block.style || {},
      };

    case "file":
      return {
        ...baseData,
        url: block.url || "",
        fileName: block.fileName || "",
        fileSize: block.fileSize || 0,
        mimeType: block.mimeType || "",
        style: block.style || {},
      };

    case "heading":
    case "subheading":
    case "paragraph":
    default:
      return {
        ...baseData,
        content: block.content || "",
        style: block.style || {},
      };
  }
};

/**
 * Transform backend response data for frontend consumption
 * This ensures all necessary fields are available for the frontend components
 */
export const transformBackendDataForFrontend = (backendData) => {
  if (!backendData) {
    console.warn(
      "transformBackendDataForFrontend: received null/undefined data"
    );
    return null;
  }

  const baseBlock = {
    id: backendData.id,
    contentId: backendData.contentId || backendData.id,
    type: backendData.type,
    display_order: backendData.display_order || 0,
    column: backendData.column || null,
    parent_block_id: backendData.parent_block_id || null,
    created_at: backendData.created_at,
    updated_at: backendData.updated_at,
    is_active:
      backendData.is_active !== undefined ? backendData.is_active : true,
  };

  // Handle two-column layout
  if (
    backendData.layout === "two-column" ||
    backendData.type === "two-column"
  ) {
    return {
      ...baseBlock,
      layout: "two-column",
      leftBlock: backendData.leftBlock
        ? transformBackendDataForFrontend(backendData.leftBlock)
        : null,
      rightBlock: backendData.rightBlock
        ? transformBackendDataForFrontend(backendData.rightBlock)
        : null,
    };
  }

  // Add type-specific fields with defaults
  const typeSpecificData = {
    content: backendData.content || "",
    url: backendData.url || "",
    fileName: backendData.fileName || "",
    fileSize: backendData.fileSize || 0,
    mimeType: backendData.mimeType || "",
    alt: backendData.alt || "",
    caption: backendData.caption || "",
    title: backendData.title || "",
    thumbnail: backendData.thumbnail || "",
    style: backendData.style || {},
  };

  return {
    ...baseBlock,
    ...typeSpecificData,
  };
};

/**
 * Create a new empty block of the specified type
 */
export const createEmptyBlock = (type, options = {}) => {
  const baseBlock = {
    id:
      options.id ||
      `temp-${Date.now()}-${Math.floor(Math.random() * 1000)
        .toString(36)
        .substr(2, 9)}`,
    type,
    display_order: options.display_order || 0,
    column: options.column || null,
    parent_block_id: options.parent_block_id || null,
    style: {},
  };

  switch (type) {
    case "image":
      return {
        ...baseBlock,
        content: "",
        url: "",
        alt: "",
        caption: "",
      };

    case "video":
      return {
        ...baseBlock,
        content: "",
        url: "",
        title: "",
        thumbnail: "",
      };

    case "file":
      return {
        ...baseBlock,
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        mimeType: "",
      };

    case "heading":
    case "subheading":
    case "paragraph":
    default:
      return {
        ...baseBlock,
        content: "",
      };
  }
};

/**
 * Validate block data before sending to backend
 */
export const validateBlockData = (block) => {
  const errors = [];

  if (!block.type) {
    errors.push("Block type is required");
  }

  // Validate display_order
  if (typeof block.display_order !== "number") {
    errors.push("display_order must be a number");
  }

  // Validate column if present
  if (block.column && !["left", "right"].includes(block.column)) {
    errors.push("column must be 'left' or 'right' if specified");
  }

  switch (block.type) {
    case "image":
      if (!block.url && !block.content) {
        errors.push("Image URL or content is required");
      }
      break;

    case "video":
      if (!block.url && !block.content) {
        errors.push("Video URL or content is required");
      }
      break;

    case "file":
      if (!block.url && !block.fileName) {
        errors.push("File URL or filename is required");
      }
      break;

    case "heading":
    case "subheading":
    case "paragraph":
      // Allow empty content for new blocks
      break;

    case "two-column":
      if (!block.leftBlock && !block.rightBlock) {
        errors.push("Two-column block must have at least one child block");
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Debug function to log block data transformation
 */
export const debugBlockTransformation = (block, direction = "to backend") => {
  console.group(`🔄 Block transformation (${direction})`);
  console.log("Original block:", block);

  if (direction === "to backend") {
    const transformed = prepareBlockDataForBackend(block);
    console.log("Transformed for backend:", transformed);

    const validation = validateBlockData(transformed);
    if (!validation.isValid) {
      console.error("❌ Validation errors:", validation.errors);
    } else {
      console.log("✅ Validation passed");
    }
  }

  console.groupEnd();
};

/**
 * Safely prepare blocks array for bulk operations
 */
export const prepareBulkBlocksForBackend = (blocks, adminId) => {
  if (!Array.isArray(blocks)) {
    throw new Error("Blocks must be an array");
  }

  if (!adminId) {
    throw new Error("Admin ID is required for bulk operations");
  }

  return blocks.map((block, index) => {
    try {
      const prepared = prepareBlockDataForBackend({
        ...block,
        display_order: index, // Ensure sequential ordering
      });

      // Validate each block
      const validation = validateBlockData(prepared);
      if (!validation.isValid) {
        console.error(`Block ${index} validation failed:`, validation.errors);
        // Return a safe fallback block
        return {
          type: "paragraph",
          content: block.content || "",
          display_order: index,
        };
      }

      return prepared;
    } catch (error) {
      console.error(`Error preparing block ${index}:`, error);
      // Return a safe fallback block
      return {
        type: "paragraph",
        content: block.content || "",
        display_order: index,
      };
    }
  });
};
